/**
 * Date helpers shared by the refinement-memory create/edit modals and
 * the list row's expiry display.
 *
 * The product surface is "expires on calendar day X" — the operator
 * doesn't think in timezones. To keep round-tripping lossless we treat
 * the picked date as a calendar day in UTC:
 *
 *   - Form `<input type="date">` value `"2026-01-05"` is read as
 *     UTC midnight (`new Date('2026-01-05')` already does this — the
 *     ES spec treats date-only ISO strings as UTC).
 *   - When formatting an ISO timestamp back into a `<input type="date">`
 *     value OR a human-readable label, we use UTC getters so the same
 *     calendar day comes out.
 *
 * Without this, a user in US Pacific picks "Jan 5", we store
 * `2026-01-05T00:00:00Z`, and the form re-reads it via `getDate()` as
 * `Jan 4` (because UTC midnight is 4pm Jan 4 local). The bug was
 * silent because the form Save button only enables on `dirty`, and the
 * round-trip mismatch made the value perma-dirty.
 */

/**
 * ISO datetime (or a `Date`, when the caller's Zod type infers z.date() but
 * ky's `.json()` actually hands back a JSON string at runtime — see the
 * unified memory/skill types) → `YYYY-MM-DD` for `<input type="date">`, in UTC.
 */
export function isoToDateInput(iso: string | Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * `YYYY-MM-DD` from a `<input type="date">` → ISO datetime at UTC
 * midnight. Returns `null` for empty/invalid input so the caller can
 * pass that straight through to the API (the contract's
 * `expiresAt: nullable().optional()` accepts null to clear).
 */
export function dateInputToIso(value: string): string | null {
  if (!value) return null;
  // `new Date('YYYY-MM-DD')` parses as UTC midnight per the ES spec.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Render an ISO datetime as a short human-readable date label,
 * using UTC so it matches the calendar day the operator picked
 * regardless of viewer timezone.
 */
export function formatMemoryDate(iso: string | Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
